﻿export class CartDatabase
{
    static CartStoreName = "CartStore";
    static CartVersionStoreName = "CartVersionStore";

    #db = null;

    constructor(db)
    {
        this.#db = db;
    }

    Close()
    {
        if (this.#db)
        {
            this.#db.close();
            this.#db = null;
        }
    }

    IsClosed()
    {
        return this.#db === null;
    }

    static OpenDatabaseAsync()
    {
        const DB_NAME = "BannerlordOnlineShopDB";
        const DB_VERSION = 1;

        return new Promise((resolve, reject) => {

            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onupgradeneeded = (event) => {

                const db = event.target.result;

                if (!db.objectStoreNames.contains(CartDatabase.CartVersionStoreName))
                {
                    db.createObjectStore(CartDatabase.CartVersionStoreName, {keyPath: "id"});
                }

                if (!db.objectStoreNames.contains(CartDatabase.CartStoreName))
                {
                    db.createObjectStore(CartDatabase.CartStoreName, {keyPath: "id"});
                }
            };

            request.onsuccess = (event) => resolve(new CartDatabase(event.target.result));
            request.onerror = (event) => reject(event.target.error);
        });
    }

    GetVersionAsync()
    {
        return new Promise((resolve, reject) => {

            if (this.IsClosed())
            {
                reject(new Error("Database is closed!"));
                return;
            }

            const transaction = this.#db.transaction([CartDatabase.CartVersionStoreName], 'readonly');

            const cartVersionStore = transaction.objectStore(CartDatabase.CartVersionStoreName);

            const versionGetRequest = cartVersionStore.get(0);

            versionGetRequest.onsuccess = () => {

                const versionItem = versionGetRequest.result;

                if (versionItem)
                {
                    resolve(versionItem.version);
                }
                else
                {
                    resolve(0);
                }
            };

            transaction.onerror = (event) => {
                reject(event.target.error || transaction.error);
            };

            transaction.onabort = () => {
                reject(new Error("Transaction aborted!"));
            };
        });
    }

    GetProductAmountAsync(id)
    {
        return new Promise((resolve, reject) => {

            if (this.IsClosed())
            {
                reject(new Error("Database is closed!"));
                return;
            }

            const transaction = this.#db.transaction([CartDatabase.CartStoreName], 'readonly');

            const cartStore = transaction.objectStore(CartDatabase.CartStoreName);

            const productGetRequest = cartStore.get(id);

            productGetRequest.onsuccess = () => {

                const productItem = productGetRequest.result;

                if (productItem)
                {
                    resolve(productItem.amount);
                }
                else
                {
                    resolve(0);
                }
            };

            transaction.onerror = (event) => {
                reject(event.target.error || transaction.error);
            };

            transaction.onabort = () => {
                reject(new Error("Transaction aborted!"));
            };
        });
    }

    SetProductAsync(id, amount)
    {
        return new Promise((resolve, reject) => {

            if (this.IsClosed())
            {
                reject(new Error("Database is closed!"));
                return;
            }

            const transaction = this.#db.transaction([CartDatabase.CartVersionStoreName, CartDatabase.CartStoreName], 'readwrite');

            const cartVersionStore = transaction.objectStore(CartDatabase.CartVersionStoreName);
            const cartStore = transaction.objectStore(CartDatabase.CartStoreName);

            const versionGetRequest = cartVersionStore.get(0);
            versionGetRequest.onsuccess = () => {

                const versionItem = versionGetRequest.result;

                const newVersion = versionItem ? versionItem.version + 1 : 1;

                cartVersionStore.put({id: 0, version: newVersion});
            };

            if (amount > 0)
            {
                cartStore.put({id: id, amount: amount});
            }
            else
            {
                cartStore.delete(id);
            }

            transaction.oncomplete = () => {
                resolve();
            };

            transaction.onerror = (event) => {
                reject(event.target.error || transaction.error);
            };

            transaction.onabort = () => {
                reject(new Error("Transaction aborted!"));
            };
        });
    }

    AddProductAmountAsync(id, amount)
    {
        return new Promise((resolve, reject) => {

            if (this.IsClosed())
            {
                reject(new Error("Database is closed!"));
                return;
            }

            const transaction = this.#db.transaction([CartDatabase.CartVersionStoreName, CartDatabase.CartStoreName], 'readwrite');

            const cartVersionStore = transaction.objectStore(CartDatabase.CartVersionStoreName);
            const cartStore = transaction.objectStore(CartDatabase.CartStoreName);

            const versionGetRequest = cartVersionStore.get(0);
            versionGetRequest.onsuccess = () => {

                const versionItem = versionGetRequest.result;

                const newVersion = versionItem ? versionItem.version + 1 : 1;

                cartVersionStore.put({id: 0, version: newVersion});
            };

            const productGetRequest = cartStore.get(id);

            productGetRequest.onsuccess = () => {

                const productItem = productGetRequest.result;

                if (productItem)
                {
                    const newAmount = productItem.amount + amount;

                    if (newAmount > 0)
                    {
                        cartStore.put({id: id, amount: newAmount});
                    }
                    else
                    {
                        cartStore.delete(id);
                    }
                }
                else
                {
                    if (amount > 0)
                    {
                        cartStore.put({id: id, amount: amount});
                    }
                }
            };

            transaction.oncomplete = () => {
                resolve();
            };

            transaction.onerror = (event) => {
                reject(event.target.error || transaction.error);
            };

            transaction.onabort = () => {
                reject(new Error("Transaction aborted!"));
            };
        });
    }

    ClearProductAsync(id)
    {
        return this.SetProductAsync(id, 0);
    }

    GetAllProductsAsync()
    {
        return new Promise((resolve, reject) => {

            if (this.IsClosed())
            {
                reject(new Error("Database is closed!"));
                return;
            }

            const transaction = this.#db.transaction([CartDatabase.CartStoreName], 'readonly');
            const cartStore = transaction.objectStore(CartDatabase.CartStoreName);

            const request = cartStore.getAll();

            request.onsuccess = (event) => resolve(event.target.result);

            transaction.onerror = (event) => {
                reject(event.target.error || transaction.error);
            };

            transaction.onabort = () => {
                reject(new Error("Transaction aborted!"));
            };
        });
    }

    GetProductsAmountSumAsync()
    {
        return new Promise((resolve, reject) => {

            if (this.IsClosed())
            {
                reject(new Error("Database is closed!"));
                return;
            }

            const transaction = this.#db.transaction([CartDatabase.CartStoreName], 'readonly');
            const cartStore = transaction.objectStore(CartDatabase.CartStoreName);

            const cursorRequest = cartStore.openCursor();

            let totalSum = 0;

            cursorRequest.onsuccess = (e) => {

                const cursor = e.target.result;

                if (cursor)
                {
                    totalSum += cursor.value.amount;

                    cursor.continue();
                }
                else
                {
                    resolve(totalSum);
                }
            };

            transaction.onerror = (event) => {
                reject(event.target.error || transaction.error);
            };

            transaction.onabort = () => {
                reject(new Error("Transaction aborted!"));
            };
        });
    }
}